import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header2';
import Icon from '../../components/AppIcon';
import WelcomeSection from './components/WelcomeSection';
import QuickActionCards from './components/QuickActionCards';
import RecentOrdersSection from './components/RecentOrdersSection';
import WishlistPreview from './components/WishlistPreview';
import AccountSummary from './components/AccountSummary';
import PromotionalOffers from './components/PromotionalOffers';
import PersonalizedRecommendations from './components/PersonalizedRecommendations';
import AccountSidebar from './components/AccountSidebar';

const UserAccountDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);

  // Mock user data
  const user = {
    firstName: "Sarah",
    lastName: "Johnson",
    email: "sarah.johnson@email.com",
    membershipLevel: "Gold",
    profileCompletion: 85,
    emailVerified: true,
    twoFactorEnabled: false,
    memberSince: "January 2022"
  };

  // Mock stats data
  const stats = {
    recentOrders: 12,
    wishlistCount: 8,
    savedAddresses: 3,
    profileCompletion: 85
  };

  // Mock recent orders
  const recentOrders = [
    {
      id: 1,
      orderNumber: "ORD-2025-001234",
      date: "July 18, 2025",
      status: "Delivered",
      itemCount: 3,
      total: 129.99
    },
    {
      id: 2,
      orderNumber: "ORD-2025-001235",
      date: "July 15, 2025",
      status: "Shipped",
      itemCount: 1,
      total: 49.99
    },
    {
      id: 3,
      orderNumber: "ORD-2025-001236",
      date: "July 12, 2025",
      status: "Processing",
      itemCount: 2,
      total: 89.50
    }
  ];

  // Mock wishlist items
  const wishlistItems = [
    {
      id: 1,
      name: "Wireless Bluetooth Headphones",
      price: 79.99,
      originalPrice: 99.99,
      discount: 20,
      image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop",
      rating: 4.5,
      reviews: 128
    },
    {
      id: 2,
      name: "Premium Cotton T-Shirt",
      price: 24.99,
      originalPrice: 34.99,
      discount: 29,
      image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=300&fit=crop",
      rating: 4.2,
      reviews: 89
    },
    {
      id: 3,
      name: "Organic Face Moisturizer",
      price: 32.99,
      image: "https://images.unsplash.com/photo-1556228720-195a672e8a03?w=300&h=300&fit=crop",
      rating: 4.8,
      reviews: 245
    }
  ];

  // Mock security info
  const securityInfo = {
    lastLogin: "July 20, 2025 at 9:15 AM",
    passwordStrength: "Strong"
  };

  // Mock promotional offers
  const promotionalOffers = [
    {
      id: 1,
      title: "Summer Sale",
      description: "Get 30% off on all summer collection items",
      discount: "30% OFF",
      code: "SUMMER30",
      expiryDate: "July 31, 2025",
      icon: "Sun",
      badge: "Limited Time"
    },
    {
      id: 2,
      title: "Free Shipping",
      description: "Free shipping on orders over $50",
      discount: "FREE",
      code: "FREESHIP",
      expiryDate: "August 15, 2025",
      icon: "Truck",
      badge: "New"
    }
  ];

  // Mock personalized recommendations
  const personalizedRecommendations = [
    {
      id: 1,
      name: "Smart Fitness Watch",
      category: "Electronics",
      price: 199.99,
      originalPrice: 249.99,
      discount: 20,
      image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=300&fit=crop",
      rating: 4.6,
      reviews: 342,
      reason: "Popular",
      confidence: 92
    },
    {
      id: 2,
      name: "Leather Crossbody Bag",
      category: "Fashion",
      price: 89.99,
      image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=300&h=300&fit=crop",
      rating: 4.4,
      reviews: 156,
      reason: "Trending",
      confidence: 88
    },
    {
      id: 3,
      name: "Aromatherapy Diffuser",
      category: "Home & Garden",
      price: 45.99,
      originalPrice: 59.99,
      discount: 23,
      image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=300&h=300&fit=crop",
      rating: 4.7,
      reviews: 203,
      reason: "For You",
      confidence: 85
    }
  ];

  const handleAddToCart = (item) => {
    setCartItems(prev => [...prev, { ...item, quantity: 1 }]);
    // Show success message or notification
    console.log(`Added ${item.name} to cart`);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Close sidebar on route change or outside click
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16 flex">
        {/* Sidebar */}
        <AccountSidebar 
          user={user}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        
        {/* Main Content */}
        <div className="flex-1 lg:ml-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Mobile Menu Button */}
            <div className="lg:hidden mb-4">
              <button
                onClick={toggleSidebar}
                className="flex items-center space-x-2 p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="Menu" size={20} />
                <span>Account Menu</span>
              </button>
            </div>

            {/* Welcome Section */}
            <WelcomeSection user={user} />

            {/* Quick Action Cards */}
            <QuickActionCards stats={stats} />

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Left Column - Main Content */}
              <div className="xl:col-span-2 space-y-6">
                <RecentOrdersSection orders={recentOrders} />
                <WishlistPreview 
                  wishlistItems={wishlistItems}
                  onAddToCart={handleAddToCart}
                />
                <PersonalizedRecommendations 
                  recommendations={personalizedRecommendations}
                  onAddToCart={handleAddToCart}
                />
              </div>

              {/* Right Column - Sidebar Content */}
              <div className="space-y-6">
                <AccountSummary 
                  user={user}
                  securityInfo={securityInfo}
                />
                <PromotionalOffers offers={promotionalOffers} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAccountDashboard;